/** @typedef {{ id?: string, src?: string, grad?: string, t: string, loc: string, yr?: string, cap: string, kw?: string[], desc?: string, portrait?: boolean, h?: number, focusX?: number, focusY?: number }} Photo */

export const GRADIENTS = {
  mono1: "linear-gradient(155deg,#15140f 0%,#3a382f 52%,#7c7766 100%)",
  mono2: "linear-gradient(160deg,#1d1c18 0%,#4a463d 55%,#9a9385 100%)",
  mono3: "linear-gradient(150deg,#0f0f0e 0%,#33322c 50%,#666056 100%)",
  sav1: "linear-gradient(155deg,#241c10 0%,#5a4628 52%,#9a7c44 100%)",
  sav2: "linear-gradient(160deg,#3a2a14 0%,#6e5022 55%,#b98a3a 100%)",
  sav3: "linear-gradient(150deg,#1c1810 0%,#46381f 52%,#7c6638 100%)",
  coast1: "linear-gradient(160deg,#1d2e36 0%,#3f5662 52%,#8aa0a2 100%)",
  coast2: "linear-gradient(155deg,#16242b 0%,#345059 55%,#7796a0 100%)",
  azul: "linear-gradient(160deg,#17303f 0%,#2f5f7a 55%,#6f9ab2 100%)",
  terra: "linear-gradient(150deg,#5a2418 0%,#9a4a2a 52%,#d39a5a 100%)",
  desert1: "linear-gradient(150deg,#3a2410 0%,#7a4f22 52%,#c89a52 100%)",
  desert2: "linear-gradient(155deg,#2a1c0e 0%,#5e3f1c 55%,#a87c3c 100%)",
  moto1: "linear-gradient(150deg,#141008 0%,#3a2a14 55%,#6a5024 100%)",
  moto2: "linear-gradient(155deg,#101010 0%,#2e2c28 52%,#5a564e 100%)",
};

/** @param {string} grad @param {string} t @param {string} [sub] @param {number} [h] @returns {Photo} */
export function placeholder(grad, t, sub = "", h = 0) {
  return { grad, t, loc: sub, yr: "", cap: t, h: h || 0 };
}

/** @type {Record<string, Photo>} */
export const PHOTOS = {
  moto: {
    src: "cjmeister-com-_DSC4977-Edit-Edit.jpg",
    t: "Forest Enduro",
    loc: "Serra da Estrela, PT",
    yr: "2023",
    cap: "BMW R-series · forest enduro",
    kw: ["Motorcycles", "Portugal", "Black & White"],
    desc: "Shot on a cold morning ride through the pines above Manteigas — the bike paused just long enough to catch the light breaking through the canopy.",
  },
  rhino: {
    src: "rhino-hero.jpg",
    t: "The Last Light",
    loc: "Lewa, Kenya",
    yr: "2022",
    cap: "White rhino · last light",
    kw: ["Wildlife", "Kenya", "Black & White"],
    desc: "One of the last northern white rhinos on earth, photographed under armed guard at dusk. A quiet, weighty few minutes I won't forget.",
  },
  rhino2: {
    src: "cjmeister-com-_CJ_1757.jpg",
    t: "Old Soul",
    loc: "Ol Pejeta, Kenya",
    yr: "2022",
    cap: "White rhino, grazing",
    kw: ["Wildlife", "Kenya"],
  },
  eagle: {
    src: "cjmeister-com-_CJ00463.jpg",
    t: "Apex",
    loc: "Studio",
    yr: "2021",
    cap: "Bald eagle",
    kw: ["Wildlife", "Portraits"],
  },
  owl: {
    src: "cjmeister-com-_CJ00911.jpg",
    t: "Night Watch",
    loc: "Studio",
    yr: "2021",
    cap: "Ural owl",
    kw: ["Wildlife", "Portraits"],
  },
  kite: {
    src: "cjmeister-com-_CJ09300.jpg",
    t: "Yellow Eye",
    loc: "Studio",
    yr: "2021",
    cap: "Yellow-billed kite",
    kw: ["Wildlife", "Portraits"],
  },
  child: {
    src: "cjmeister-com-06076.jpg",
    t: "Tenderness",
    loc: "Portugal",
    yr: "2020",
    cap: "A quiet moment",
    kw: ["Portraits", "Portugal", "Black & White"],
  },
  london: {
    src: "cjmeister-com-_CJ08407.jpg",
    t: "Tower",
    loc: "London, UK",
    yr: "2019",
    cap: "Tower Bridge, from below",
    kw: ["Street Photography", "Black & White"],
  },
  nude: {
    src: "cjmeister-com-_DSC6660-Edit.jpg",
    t: "Form",
    loc: "Studio",
    yr: "2020",
    cap: "Figure study",
    portrait: true,
  },
};

const g = GRADIENTS;
const ph = placeholder;

/** @typedef {{ id: string, title: string, subtitle: string, count: number, cover: Photo, photos: Photo[] }} Collection */

/** @type {Collection[]} */
export const COLLECTIONS = [
  {
    id: "bw",
    title: "Black & White",
    subtitle: "Monochrome studies across a decade.",
    count: 42,
    cover: PHOTOS.moto,
    photos: [
      PHOTOS.moto,
      PHOTOS.rhino,
      PHOTOS.child,
      PHOTOS.london,
      PHOTOS.nude,
      ph(g.mono2, "Crossing", "Lisbon", 300),
      ph(g.mono1, "Still", "Studio", 360),
      ph(g.mono3, "Rooftops", "Porto", 240),
      ph(g.mono2, "Hands", "Portrait", 300),
      ph(g.mono1, "Rain", "Street", 260),
      ph(g.mono3, "Horizon", "Atlantic", 230),
      ph(g.mono2, "Stillness", "Studio", 340),
    ],
  },
  {
    id: "kenya",
    title: "Kenya",
    subtitle: "Two weeks on the Laikipia conservancies.",
    count: 58,
    cover: PHOTOS.rhino,
    photos: [
      PHOTOS.rhino,
      PHOTOS.rhino2,
      PHOTOS.kite,
      ph(g.sav1, "Acacia at Dawn", "Laikipia", 260),
      ph(g.sav2, "The Herd", "Ol Pejeta", 300),
      ph(g.sav3, "Ranger", "Lewa", 360),
      ph(g.sav1, "Dust", "Conservancy", 240),
      ph(g.sav2, "Giraffe", "Savannah", 360),
      ph(g.sav3, "Last Two", "Ol Pejeta", 280),
      ph(g.sav1, "Watering Hole", "Laikipia", 260),
      ph(g.sav2, "Golden Hour", "Savannah", 300),
    ],
  },
  {
    id: "portugal",
    title: "Portugal",
    subtitle: "Notes from a new home on the Atlantic.",
    count: 47,
    cover: ph(g.coast1, "Cabo Roca", "Atlantic coast", 0),
    photos: [
      PHOTOS.child,
      ph(g.azul, "Azulejo", "Lisbon", 300),
      ph(g.coast1, "Cliffs", "Sagres", 260),
      ph(g.terra, "Tram 28", "Lisbon", 360),
      ph(g.coast2, "Harbour", "Cascais", 240),
      ph(g.azul, "Tiles", "Porto", 300),
      ph(g.coast1, "Surf", "Nazaré", 360),
      ph(g.terra, "Rooftops", "Alfama", 240),
      ph(g.coast2, "Fado Night", "Lisbon", 300),
      ph(g.azul, "Doorway", "Sintra", 260),
    ],
  },
  {
    id: "dakar",
    title: "O Nosso Dakar",
    subtitle: "Chasing the rally across the dunes.",
    count: 134,
    cover: PHOTOS.moto,
    photos: [
      PHOTOS.moto,
      ph(g.desert1, "The Dunes", "Stage 6", 260),
      ph(g.desert2, "Bivouac", "Camp", 300),
      ph(g.desert1, "Navigation", "Roadbook", 360),
      ph(g.moto1, "Throttle", "Stage 4", 240),
      ph(g.desert2, "Dust Trail", "Open desert", 300),
      ph(g.desert1, "Sunrise Start", "Bivouac", 360),
      ph(g.moto1, "Repairs", "Camp", 260),
      ph(g.desert2, "The Finish", "Stage 12", 280),
      ph(g.desert1, "Horizon", "Erg", 300),
    ],
  },
  {
    id: "wildlife",
    title: "Wildlife",
    subtitle: "Eye to eye with the wild.",
    count: 96,
    cover: PHOTOS.eagle,
    photos: [
      PHOTOS.eagle,
      PHOTOS.owl,
      PHOTOS.kite,
      PHOTOS.rhino,
      PHOTOS.rhino2,
      ph(g.sav2, "In Flight", "Rift Valley", 260),
      ph(g.mono3, "Watcher", "Forest", 300),
      ph(g.sav1, "Mane", "Maasai Mara", 360),
      ph(g.sav3, "Tusker", "Amboseli", 240),
      ph(g.mono2, "Plumage", "Studio", 300),
      ph(g.sav2, "Stare", "Savannah", 260),
    ],
  },
  {
    id: "moto",
    title: "Motorcycles",
    subtitle: "Machines, mud and the open road.",
    count: 71,
    cover: PHOTOS.moto,
    photos: [
      PHOTOS.moto,
      ph(g.moto2, "The Tank", "Workshop", 300),
      ph(g.moto1, "Helmet", "Studio", 260),
      ph(g.moto2, "Open Road", "Serra", 360),
      ph(g.moto1, "Mud", "Enduro", 240),
      ph(g.moto2, "Chrome", "Detail", 300),
      ph(g.moto1, "Dusk Ride", "Highway", 360),
      ph(g.moto2, "Workshop", "Garage", 260),
    ],
  },
];

/** @param {string} id */
export function getCollectionById(id) {
  return COLLECTIONS.find((collection) => collection.id === id);
}
